import React from 'react';
import PropTypes from 'prop-types';
import { getColorForValue } from '../../Utils/ColorUtils';

// Individual skill progress bar component
const SkillProgressBar = ({ skill, grade, rationale, compact = false }) => {
  // Ensure grade is between 0 and 10
  const numericGrade = Math.min(10, Math.max(0, grade));
  
  // Calculate width percentage
  const widthPercentage = (numericGrade / 10) * 100;
  
  // Get color based on grade
  const barColor = getColorForValue(numericGrade);
  
  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className={`font-medium text-gray-800 ${compact ? 'text-sm' : 'text-sm'}`}>{skill}</span>
        <span className={`font-medium text-gray-700 ${compact ? 'text-sm' : 'text-sm'}`}>{numericGrade}</span>
      </div>
      <div className={`w-full bg-gray-200 rounded-full ${compact ? 'h-4 mb-3' : 'h-4 mb-2'}`}>
        <div 
          className={`rounded-full transition-all duration-300 ${compact ? 'h-4' : 'h-4'}`}
          style={{ 
            width: `${widthPercentage}%`,
            backgroundColor: barColor
          }}
        ></div>
      </div>
      {!compact && rationale && (
        <p className="text-xs text-gray-500">{rationale}</p>
      )}
    </div>
  );
};

// Main skills progress bars component
const SkillsProgressBars = ({ title, skills, showSections = false }) => {
  if (!skills || !Array.isArray(skills) || skills.length === 0) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200 h-full">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
        <p className="text-gray-600">No skills data available.</p>
      </div>
    );
  }

  if (showSections) {
    // Sort skills by grade (highest to lowest)
    const sortedSkills = [...skills].sort((a, b) => {
      const gradeA = typeof a.grade === 'number' ? a.grade : parseFloat(a.grade) || 0;
      const gradeB = typeof b.grade === 'number' ? b.grade : parseFloat(b.grade) || 0;
      return gradeB - gradeA;
    });

    // Get top 3 and bottom 3
    const topSkills = sortedSkills.slice(0, 3);
    const bottomSkills = sortedSkills.slice(-3).reverse(); // Reverse to show lowest first

    return (
      <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200 h-full">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">{title}</h3>
        
        {/* Side by side layout for Top 3 and Bottom 3 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Top 3 Skills */}
          <div>
            <h4 className="text-md font-medium text-gray-700 mb-3">Top 3 Skills</h4>
            <div className="space-y-6">
              {topSkills.map((skill, index) => (
                <SkillProgressBar
                  key={`top-${index}`}
                  skill={skill.skill}
                  grade={skill.grade}
                  rationale={skill.rationale}
                  compact={true}
                />
              ))}
            </div>
          </div>

          {/* Bottom 3 Skills */}
          {bottomSkills.length > 0 && (
            <div>
              <h4 className="text-md font-medium text-gray-700 mb-3">Bottom 3 Skills</h4>
              <div className="space-y-6">
                {bottomSkills.map((skill, index) => (
                  <SkillProgressBar
                    key={`bottom-${index}`}
                    skill={skill.skill}
                    grade={skill.grade}
                    rationale={skill.rationale}
                    compact={true}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default behavior - show all skills as before
  return (
    <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200 h-full">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      <div className="space-y-2">
        {skills.map((skill, index) => (
          <SkillProgressBar
            key={index}
            skill={skill.skill}
            grade={skill.grade}
            rationale={skill.rationale}
          />
        ))}
      </div>
    </div>
  );
};

SkillsProgressBars.propTypes = {
  title: PropTypes.string.isRequired,
  skills: PropTypes.arrayOf(PropTypes.shape({
    skill: PropTypes.string.isRequired,
    grade: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    rationale: PropTypes.string
  }))
};

export default SkillsProgressBars;
